import React from 'react';
import { Link } from 'react-router-dom';
import { DOWNLOADS, DownloadTarget } from '../config/downloads';

/**
 * Public download page (#665). No authentication required. Lists the desktop
 * and mobile apps; each platform shows a download/store button when a public
 * URL is configured, or "Coming soon" otherwise.
 */
const DownloadCard: React.FC<{ target: DownloadTarget }> = ({ target }) => {
  const available = !!target.url;
  const cta = target.kind === 'store' ? 'Get the app' : `Download for ${target.platform}`;

  return (
    <div className="bg-white shadow rounded-lg p-6 flex flex-col">
      <h3 className="text-lg font-semibold text-gray-900">{target.platform}</h3>
      <p className="mt-1 text-sm text-gray-500">{target.requirement}</p>
      {target.note && <p className="mt-2 text-sm text-gray-600">{target.note}</p>}
      <div className="mt-4 flex-1 flex items-end">
        {available ? (
          <a
            href={target.url}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#0F4C81] hover:bg-[#0d3f6b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A9BD9]"
            // Open in a new tab so the app isn't navigated away from — the HTML5
            // `download` attribute is ignored for cross-origin URLs (the assets
            // are hosted elsewhere), and `_blank` keeps this page open either way.
            target="_blank"
            rel="noopener noreferrer"
            {...(target.kind === 'direct' ? { download: true } : {})}
          >
            {cta}
          </a>
        ) : (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            Coming soon
          </span>
        )}
      </div>
    </div>
  );
};

const Download: React.FC = () => {
  const anyAvailable = DOWNLOADS.some((d) => !!d.url);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <span className="text-xl font-bold text-[#0F4C81]">Connect Share</span>
          <Link to="/login" className="text-sm font-medium text-[#0F4C81] hover:underline">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Download Connect Share</h1>
          <p className="mt-2 text-gray-600">
            Share network access through personal, peer-to-peer VPN connections. Free forever.
          </p>
        </div>

        {!anyAvailable && (
          <div className="mt-6 mx-auto max-w-2xl rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            Downloads aren't published yet — the apps are in final testing. Check back soon,
            or <Link to="/register" className="font-medium underline">create an account</Link> to
            use Connect Share in your browser.
          </div>
        )}

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DOWNLOADS.map((target) => (
            <DownloadCard key={target.platform} target={target} />
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-[#0F4C81] hover:underline">
            Sign in to the web app
          </Link>
          .
        </p>
      </main>
    </div>
  );
};

export default Download;
